import React, { useEffect, useState } from "react";

interface CountdownTimerProps {
  /** ISO-8601 UTC game start time, e.g. "2026-03-03T23:10:00Z" */
  gameStartTimeUtc: string | null;
  /** Backend game status: "Preview", "Live", or "Final" */
  gameStatus: string | null;
  /** Detailed game status: "Scheduled", "In Progress", "Final", "Postponed", etc. */
  statusDetailed: string | null;
}

interface TimeRemaining {
  hours: number;
  minutes: number;
  totalMs: number;
}

/** Abstract states that indicate the game is over. */
const FINAL_STATUSES = new Set(["Final"]);
/** Detailed states that indicate the game is over. */
const FINAL_DETAILED_STATUSES = new Set(["Final", "Game Over", "Completed Early"]);
/** Detailed states that indicate the game was cancelled / postponed / suspended. */
const INTERRUPTED_DETAILED_STATUSES = new Set(["Postponed", "Cancelled", "Suspended"]);

/** Estimated max game duration (4 hours). Used as a client-side heuristic when
 *  the backend status is stale (still "Preview") but the game likely finished. */
const ESTIMATED_GAME_DURATION_MS = 4 * 60 * 60 * 1000;

function getTimeRemaining(targetUtc: string): TimeRemaining {
  const nowMs = Date.now();
  const targetMs = new Date(targetUtc).getTime();
  const totalMs = targetMs - nowMs;

  if (totalMs <= 0) {
    return { hours: 0, minutes: 0, totalMs };
  }

  const totalMinutes = Math.floor(totalMs / 60_000);
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    totalMs,
  };
}

function formatCountdown(remaining: TimeRemaining): string {
  if (remaining.hours > 0) {
    return `${remaining.hours}h ${remaining.minutes}m`;
  }
  if (remaining.minutes > 0) {
    return `${remaining.minutes}m`;
  }
  return "< 1m";
}

type GameDisplayState = "countdown" | "urgent" | "live" | "final" | "postponed";

/**
 * Determine the display state of the game based on backend status + client clock.
 */
function resolveDisplayState(
  gameStatus: string | null,
  statusDetailed: string | null,
  gameStartTimeUtc: string | null,
  remaining: TimeRemaining | null,
): GameDisplayState {
  // 1. Backend says Final → Final
  if (gameStatus && FINAL_STATUSES.has(gameStatus)) return "final";
  if (statusDetailed && FINAL_DETAILED_STATUSES.has(statusDetailed)) return "final";

  // 2. Postponed / Cancelled / Suspended
  if (statusDetailed && INTERRUPTED_DETAILED_STATUSES.has(statusDetailed)) return "postponed";

  // 3. Backend says Live → Live
  if (gameStatus === "Live") return "live";

  // 4. Client-side time checks
  if (remaining && remaining.totalMs <= 0 && gameStartTimeUtc) {
    const startMs = new Date(gameStartTimeUtc).getTime();
    const elapsed = Date.now() - startMs;

    // If more than estimated game duration has passed and backend still says
    // Preview (stale artifact), show Final as a heuristic.
    if (elapsed >= ESTIMATED_GAME_DURATION_MS) return "final";

    // Otherwise the game is probably still in progress.
    return "live";
  }

  // 5. Countdown — game hasn't started yet
  if (remaining && remaining.hours === 0 && remaining.minutes > 0) return "urgent";
  return "countdown";
}

/**
 * Live countdown to first pitch. Updates every 30 seconds.
 * Shows countdown before game, "Live" during, "Final" when complete,
 * or "Postponed"/"Suspended" for interrupted games.
 */
export function CountdownTimer({ gameStartTimeUtc, gameStatus, statusDetailed }: CountdownTimerProps): JSX.Element | null {
  const [remaining, setRemaining] = useState<TimeRemaining | null>(() =>
    gameStartTimeUtc ? getTimeRemaining(gameStartTimeUtc) : null
  );

  useEffect(() => {
    if (!gameStartTimeUtc) return;

    // Initial calculation
    setRemaining(getTimeRemaining(gameStartTimeUtc));

    // Update every 30 seconds for responsive countdown
    const interval = setInterval(() => {
      setRemaining(getTimeRemaining(gameStartTimeUtc));
    }, 30_000);

    return () => clearInterval(interval);
  }, [gameStartTimeUtc]);

  if (!gameStartTimeUtc || remaining === null) {
    return null;
  }

  const displayState = resolveDisplayState(gameStatus, statusDetailed, gameStartTimeUtc, remaining);

  // Final — game is complete
  if (displayState === "final") {
    return (
      <span className="countdown-timer countdown-final" aria-label="Game is complete">
        Final
      </span>
    );
  }

  // Postponed / Cancelled / Suspended
  if (displayState === "postponed") {
    const label = statusDetailed ?? "Postponed";
    return (
      <span className="countdown-timer countdown-postponed" aria-label={`Game is ${label.toLowerCase()}`}>
        {label}
      </span>
    );
  }

  // Live — game in progress
  if (displayState === "live") {
    return (
      <span className="countdown-timer countdown-live" aria-label="Game is live">
        <span className="countdown-dot" aria-hidden="true" />
        Live
      </span>
    );
  }

  // Countdown to first pitch
  const urgencyClass = displayState === "urgent" ? "countdown-urgent" : "";

  return (
    <span
      className={`countdown-timer ${urgencyClass}`}
      aria-label={`${formatCountdown(remaining)} until first pitch`}
    >
      <svg className="countdown-icon" viewBox="0 0 16 16" width="12" height="12" fill="currentColor" aria-hidden="true">
        <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 14.4A6.4 6.4 0 1 1 8 1.6a6.4 6.4 0 0 1 0 12.8zM8.4 4H7.2v4.8l4.2 2.52.6-1-3.6-2.14V4z"/>
      </svg>
      {formatCountdown(remaining)}
    </span>
  );
}
